import React, { useEffect, useState } from 'react';
import axios from '../utils/axiosInstance';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tag, Percent, Calendar, Users } from 'lucide-react';
import { usePlan } from '@/context/PlanContext';

interface Domain {
  _id: string;
  slug: string;
  plan?: string;
}

interface DiscountSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  domains: Domain[];
  /** Called after global pricing settings are saved/removed, so the parent
   *  can refresh any locally-cached pricing state (e.g. the default price
   *  shown in the Plan Edit dialog). */
  onSettingsSaved?: () => void;
}

interface DiscountForm {
  originalPrice: string;
  discountPercent: string;
  annualDiscountPercent: string;
  discountAppliesTo: 'all' | 'selected';
  discountDomains: string[];
}

const DiscountSettingsModal: React.FC<DiscountSettingsModalProps> = ({
  open,
  onOpenChange,
  domains,
  onSettingsSaved,
}) => {
  const { refreshPlanInfo } = usePlan();
  const [form, setForm] = useState<DiscountForm>({
    originalPrice: '',
    discountPercent: '0',
    annualDiscountPercent: '20',
    discountAppliesTo: 'all',
    discountDomains: [],
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios
      .get('/api/domain/settings/global')
      .then((res) => {
        const s = res.data;
        setForm({
          originalPrice: s.originalPrice != null ? String(s.originalPrice) : '',
          discountPercent: String(s.discountPercent ?? 0),
          annualDiscountPercent: String(s.annualDiscountPercent ?? 20),
          discountAppliesTo: s.discountAppliesTo || 'all',
          discountDomains: s.discountDomains || [],
        });
      })
      .catch(() => toast.error('Failed to load discount settings'))
      .finally(() => setLoading(false));
  }, [open]);

  const origNum = parseFloat(form.originalPrice) || 0;
  const discPct = Math.max(0, Math.min(99, parseFloat(form.discountPercent) || 0));
  const annualPct = Math.max(0, Math.min(99, parseFloat(form.annualDiscountPercent) || 0));
  const computedMonthly = origNum > 0 && discPct > 0
    ? Math.max(1, Math.floor(origNum * (1 - discPct / 100)))
    : origNum > 0 ? origNum : null;
  const computedAnnual = computedMonthly != null
    ? Math.max(1, Math.floor(computedMonthly * 12 * (1 - annualPct / 100)))
    : null;
  const annualSavings = computedMonthly != null && computedAnnual != null
    ? computedMonthly * 12 - computedAnnual
    : null;

  const toggleDomain = (slug: string) => {
    setForm((prev) => ({
      ...prev,
      discountDomains: prev.discountDomains.includes(slug)
        ? prev.discountDomains.filter((s) => s !== slug)
        : [...prev.discountDomains, slug],
    }));
  };

  const handleSave = async () => {
    if (!form.originalPrice || origNum <= 0) {
      toast.error('Enter a valid original price');
      return;
    }
    if (form.discountAppliesTo === 'selected' && form.discountDomains.length === 0) {
      toast.error('Select at least one domain');
      return;
    }
    setSaving(true);
    try {
      await axios.put('/api/domain/settings/global', {
        originalPrice: origNum,
        discountPercent: discPct,
        annualDiscountPercent: annualPct,
        discountAppliesTo: form.discountAppliesTo,
        discountDomains: form.discountAppliesTo === 'selected' ? form.discountDomains : [],
      });
      toast.success('Discount settings saved');
      // Refresh PlanContext so the UpgradeModal / plan-info reflect the new
      // global price immediately (no manual reload needed).
      refreshPlanInfo();
      onSettingsSaved?.();
      onOpenChange(false);
    } catch {
      toast.error('Failed to save discount settings');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveDiscount = async () => {
    setSaving(true);
    try {
      await axios.put('/api/domain/settings/global', {
        originalPrice: null,
        discountPercent: 0,
        discountAppliesTo: 'all',
        discountDomains: [],
      });
      setForm((prev) => ({ ...prev, originalPrice: '', discountPercent: '0', discountDomains: [] }));
      toast.success('Discount removed');
      refreshPlanInfo();
      onSettingsSaved?.();
    } catch {
      toast.error('Failed to remove discount');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-130 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-primary" />
            Discount Management
          </DialogTitle>
          <DialogDescription>
            Set a launch offer or promotional discount. The computed price is automatically applied as the default plan price.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="space-y-5 mt-2">

            {/* Monthly Pricing */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5">
                  Original Price (₹/month)
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">₹</span>
                  <Input
                    type="number"
                    min="1"
                    className="pl-7"
                    value={form.originalPrice}
                    onChange={(e) => setForm((p) => ({ ...p, originalPrice: e.target.value }))}
                    placeholder="e.g. 1999"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">Shown as strikethrough</p>
              </div>
              <div>
                <Label className="mb-1.5">
                  <Percent className="w-3.5 h-3.5" /> Discount %
                </Label>
                <Input
                  type="number"
                  min="0"
                  max="99"
                  value={form.discountPercent}
                  onChange={(e) => setForm((p) => ({ ...p, discountPercent: e.target.value }))}
                  placeholder="e.g. 70"
                />
                <p className="text-xs text-muted-foreground mt-1">Set to 0 to hide discount</p>
              </div>
            </div>

            {/* Live preview */}
            {computedMonthly != null && (
              <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 text-sm">
                <div className="font-medium text-primary mb-1">Monthly price preview</div>
                <div className="flex items-center gap-2 flex-wrap">
                  {discPct > 0 && (
                    <span className="line-through text-muted-foreground">
                      ₹{origNum.toLocaleString('en-IN')}
                    </span>
                  )}
                  <span className="text-xl font-bold">
                    ₹{computedMonthly.toLocaleString('en-IN')}
                  </span>
                  <span className="text-muted-foreground">/month</span>
                  {discPct > 0 && (
                    <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                      {discPct}% off
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Annual billing */}
            <div>
              <Label className="mb-1.5">
                <Calendar className="w-3.5 h-3.5" /> Annual Discount %
              </Label>
              <Input
                type="number"
                min="0"
                max="99"
                value={form.annualDiscountPercent}
                onChange={(e) => setForm((p) => ({ ...p, annualDiscountPercent: e.target.value }))}
                placeholder="e.g. 20"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Additional % off when customer chooses annual billing
              </p>
              {computedAnnual != null && (
                <div className="mt-2 p-2.5 rounded-md bg-muted/40 text-sm flex items-center gap-2 flex-wrap">
                  <span className="font-medium">Annual total:</span>
                  <span className="font-bold">₹{computedAnnual.toLocaleString('en-IN')}/yr</span>
                  <span className="text-muted-foreground">
                    (≈ ₹{Math.round(computedAnnual / 12).toLocaleString('en-IN')}/mo)
                  </span>
                  {annualSavings != null && annualSavings > 0 && (
                    <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                      Save ₹{annualSavings.toLocaleString('en-IN')}/yr
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Scope */}
            <div>
              <Label className="mb-2">
                <Users className="w-3.5 h-3.5" /> Applies To
              </Label>
              <Tabs
                value={form.discountAppliesTo}
                onValueChange={(v) => setForm((p) => ({ ...p, discountAppliesTo: v as 'all' | 'selected' }))}
              >
                <TabsList className="w-full">
                  <TabsTrigger value="all" className="flex-1">All Domains</TabsTrigger>
                  <TabsTrigger value="selected" className="flex-1">Selected Domains</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Domain list (selected mode) */}
            {form.discountAppliesTo === 'selected' && (
              <div>
                <p className="text-xs text-muted-foreground mb-2">
                  {form.discountDomains.length} domain(s) selected
                </p>
                <div className="border border-border rounded-lg divide-y divide-border max-h-48 overflow-y-auto">
                  {domains.map((d) => (
                    <label
                      key={d._id}
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/40 text-sm"
                    >
                      <Checkbox
                        checked={form.discountDomains.includes(d.slug)}
                        onCheckedChange={() => toggleDomain(d.slug)}
                      />
                      <span className="flex-1 font-mono">{d.slug}</span>
                      {d.plan && (
                        <span
                          className={`text-xs px-1.5 py-0.5 rounded-full ${
                            d.plan === 'paid' || d.plan === 'pro' || d.plan === 'pro_max'
                              ? 'bg-primary/10 text-primary'
                              : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {d.plan === 'pro_max' || d.plan === 'paid' ? 'Pro Max' : d.plan === 'pro' ? 'Pro' : 'Unpaid'}
                        </span>
                      )}
                    </label>
                  ))}
                  {domains.length === 0 && (
                    <p className="text-center text-sm text-muted-foreground py-4">No domains found</p>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-1">
              <Button onClick={handleSave} disabled={saving} className="flex-1">
                {saving ? 'Saving…' : 'Save Discount Settings'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleRemoveDiscount}
                disabled={saving}
                className="text-destructive border-destructive/30 hover:bg-destructive/5"
              >
                Remove
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DiscountSettingsModal;
